import Tag from '../Tag';

/**
 * ConsentManager.Tag.GoogleTag
 *
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @lastModified 2024-11-28
 * @package felixkiesewetter-js/consentmanager
 * @since 5.0.0 Renamed Property to Tag while creating workspaces.
 */
class GoogleTag extends Tag
{

    // Private
    #id;

    // Protected
    _cookies = ['_ga', '_gid'];

    /**
     * DefaultTag constructor
     *
     * @param {Object} options
     * @returns {GoogleTag}
     */
    constructor(options)
    {
        super(options);
        if (!options.id) {
            console.error('[ConsentManager]', 'Using a property with option googleTag requires a valid tag id.'); // TODO: use Debugger
            return this;
        }
        this.setId(options.id);
        return this;
    }

    /**
     * Opt-in tag injecting the Google Tag JS and creating the window.dataLayer
     *
     * @returns {Tag}
     */
    optIn()
    {
        if (document.getElementById('gtag')) {
            return this;
        }
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js${this.getId() ? '?id=' + this.getId() : ''}`;
        script.id = 'gtag';
        document.head.appendChild(script);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() {
            dataLayer.push(arguments);
        }
        this.#awaitGoogleTag(() => {
            // Set initial consent state
            gtag('consent', 'default', {
                'analytics_storage': 'denied',
                'ad_storage': 'denied',
                'ad_user_data': 'denied',
                'ad_personalization': 'denied'
            });
            // TODO begin: this only respects analytics, also implement ads
            this.#updateConsentState(true);
            gtag('js', new Date());
            gtag('config', this.#id);
            /*
                gtag('js', new Date())
                gtag('config', 'AW-1012028949')
                gtag('event', 'conversion', { 'send_to': 'AW-1012028949/Q4HFCMm16fIBEJWsyeID' })
             */
            // TODO end.
            this.logConsentState();
        });
        return super.optIn();
    }

    // TODO: OptOut() => clear cookies + make sure no script is loaded ...

    // #######
    // Methods
    // #######

    /**
     * Run callback only if gtag() function is globally available
     *
     * @param callback
     */
    #awaitGoogleTag(callback)
    {
        if ('function' === typeof gtag) {
            callback();
            return;
        }
        setTimeout(this.#awaitGoogleTag, 100);
    }

    /**
     * Log consent state
     */
    logConsentState()
    {
        let consentState = {};
        dataLayer.filter(event => {
            if (event[0] === 'consent') {
                consentState = { ...consentState, ...event[2] };
            }
        });
        this.getConsentManager().getDebugger().log('Consent state:', consentState);
    }

    /**
     * Update consent state
     *
     * @param {null|boolean} analyticsStorage
     * @param {null|boolean} adStorage
     */
    #updateConsentState(analyticsStorage = null, adStorage = null)
    {
        if (!analyticsStorage && !adStorage) {
            return
        }
        const consent = {
            'ad_user_data': 'granted',
            'ad_personalization': 'granted'
        }
        if (true === analyticsStorage) {
            consent.analytics_storage = 'granted'
        }
        if (true === adStorage) {
            consent.ad_storage = 'granted'
        }
        gtag('consent', 'update', consent)
        this.logConsentState()
    }

    // #######
    // Getters
    // #######

    /**
     * Getter for the Google Tag id
     *
     * @returns {string}
     */
    getId()
    {
        return this.#id;
    }

    // #######
    // Setters
    // #######

    /**
     * Setter for the Google Tag id
     *
     * @param {string} id
     * @returns {GoogleTag}
     */
    setId(id)
    {
        // TODO: By checking the tag => define the type of Gtag (right now only supports analytics)
        this.#id = id;
        return this;
    }

}

export default GoogleTag;
